import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CasingCreditRepairDetails } from '../../models/casing-repair-details.model';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-casing-repair-details',
  templateUrl: './casing-credit-repair-details.component.html',
  styleUrl: './casing-credit-repair-details.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditRepairDetailsComponent implements OnInit, CanComponentDeactivate {
  @Input() casingCreditRepairPk: string;
  code: string;
  description: string;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingCreditRepairDetails>;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  initialFormValues: any = {};
  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingRepairDetails: CasingCreditRepairDetails[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  amountList: string[];
  codeList: string[] = [];
  descriptionList: string[] = [];
  isInitialPatch = true;
  isLoading = false;
  // Add a map to store all modified rows from all pages
  allModifiedRows: Map<string, any> = new Map();
  saveEnabled = false;
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CasingCreditRepairDetails-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getCasingRepairDetails(this.filterFormBackend.value, this.casingCreditRepairPk, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.casingCreditRepairDetails as CasingCreditRepairDetails[]).map((item) => ({
          repairCode: item.repairCode,
          repairDescription: item.repairDescription,
          amount: item.amount
        }))
      }))
    );

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<CasingCreditRepairDetails> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    // Store changes from current page before fetching new data
    const modifiedRows = this.getModifiedRows();
    modifiedRows.forEach((row) => {
      this.allModifiedRows.set(row.casingCreditRepairDetailPk, row);
    });

    return this.productDataService
      .getCasingRepairDetails({ ...this.filterFormBackend.value, ...filters }, this.casingCreditRepairPk, filters.page, filters.limit)
      .pipe(
        map((response: any) => {
          this.casingRepairDetails = response.data.casingCreditRepairDetails || [];
          this.pagination = response.data.pagination;
          this.updateDisplayedRowsForm(this.casingRepairDetails);
          this.displayedRowsForm.markAsPristine();
          this.cdr.markForCheck();
          return { items: this.casingRepairDetails, count: this.pagination.totalRecords };
        })
      );
  };

  constructor() {
    this.filterForm = this.fb.group({
      repairCodes: [[]],
      repairDescriptions: [[]],
      amounts: [[]]
    });
    this.filterFormBackend = this.fb.group({
      repairCodes: [[]],
      repairDescriptions: [[]],
      amounts: [[]]
    });
  }

  ngOnInit(): void {
    this.casingCreditRepairPk = this.route.snapshot.paramMap.get('casingCreditRepairPk');
    const { code, description } = history.state;
    this.code = code;
    this.description = description;
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);

    // Load filter data from API first
    this.productDataService.getCasingRepairDetailsSearchFilters(this.casingCreditRepairPk).subscribe({
      next: (response: any) => {
        this.codeList = response.data.repairCodes || [];
        this.descriptionList = response.data.repairDescriptions || [];
        this.amountList = response.data.amounts || [];

        // Initialize filter form with empty arrays (similar to casing-repair)
        this.filterForm.patchValue({
          repairCodes: [],
          repairDescriptions: [],
          amounts: []
        });
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading casing repair details filters:', error);
      }
    });

    // Setup table and form listeners
    if (this.tableRef) {
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, false);
    }
    this.setupFormListeners();
    this.configureColumns();
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      if (this.isInitialPatch) {
        this.isInitialPatch = false;
        return;
      }
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, true);
    });
    // Listen for changes in the displayedRowsForm to detect unsaved changes
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  private configureColumns(): void {
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        repairCode: this.translationService.translate('codeText'),
        repairDescription: this.translationService.translate('descriptionText'),
        amount: this.translationService.translate('AmountText')
      };

      this.tableConfig.columns = {
        code: { cell: (row) => row.repairCode, header: this.translationService.translate('codeText') },
        description: { cell: (row) => row.repairDescription, header: this.translationService.translate('descriptionText') },
        amount: { cell: 'amount', header: this.translationService.translate('AmountText') }
      };
      this.cdr.markForCheck();
    });
  }

  createRowForm(row: CasingCreditRepairDetails): FormGroup {
    const formattedAmount = this.formatAmountForDisplay(row.amount);
    return this.fb.group({
      code: [row.repairCode],
      description: [row.repairDescription],
      amount: [formattedAmount, [Validators.required, this.amountValidator()]],
      casingCreditRepairDetailPk: [row.casingCreditRepairDetailPk]
    });
  }

  private updateDisplayedRowsForm(rows: any[]): void {
    const rowForms = rows.map((row) => {
      // If this row was modified previously, use the modified value
      const modifiedRow = this.allModifiedRows.get(row.casingCreditRepairDetailPk);
      const displayValue = this.formatAmountForDisplay(modifiedRow ? modifiedRow.amount : row.amount);
      return this.fb.group({
        code: [row.repairCode],
        description: [row.repairDescription],
        amount: [displayValue, [Validators.required, this.amountValidator()]],
        casingCreditRepairDetailPk: [row.casingCreditRepairDetailPk]
      });
    });
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
    // Store initial form values to track changes (use current row data as baseline, not modified data)
    const baselineValues = rows.map((row) => ({
      code: row.repairCode,
      description: row.repairDescription,
      amount: this.formatAmountForDisplay(row.amount), // Use formatted display value
      casingCreditRepairDetailPk: row.casingCreditRepairDetailPk
    }));
    this.initialFormValues = JSON.parse(JSON.stringify({ rows: baselineValues }));
  }

  getControl(item: CasingCreditRepairDetails, controlName: string): FormControl {
    const index = this.casingRepairDetails.findIndex((row) => row.casingCreditRepairDetailPk === item.casingCreditRepairDetailPk);
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset({
      repairCodes: [],
      repairDescriptions: [],
      amounts: []
    });
  }

  generateFilterForm(): void {
    const filterCodes = this.filterForm.get('repairCodes').value || [];
    const filterDescriptions = this.filterForm.get('repairDescriptions').value || [];
    const filterAmounts = this.filterForm.get('amounts').value || [];

    this.filterFormBackend.patchValue({
      repairCodes: filterCodes,
      repairDescriptions: filterDescriptions,
      amounts: filterAmounts
    });
  }
  isFormChanged(): boolean {
    if (!this.initialFormValues || !this.displayedRowsForm) {
      return false;
    }

    const currentValues = this.displayedRowsForm.getRawValue();

    // Check if rows on current page have been modified
    if (currentValues.rows && this.initialFormValues.rows) {
      for (let i = 0; i < currentValues.rows.length; i++) {
        if (i < this.initialFormValues.rows.length) {
          // Check if the amount has changed from initial
          if (currentValues.rows[i].amount !== this.initialFormValues.rows[i].amount) {
            return true;
          }
        }
      }
    }

    // Also check if there are any unsaved modifications in allModifiedRows Map
    // But filter out any that match the current page and have been reset to original
    if (this.allModifiedRows.size > 0) {
      for (const [key] of this.allModifiedRows.entries()) {
        // Check if this row is on current page
        const currentRow = this.casingRepairDetails.find((r) => r.casingCreditRepairDetailPk === key);
        if (currentRow) {
          // If on current page and matches current form value, it's been reverted - remove it
          const formRow = currentValues.rows?.find((r: { casingCreditRepairDetailPk: string }) => r.casingCreditRepairDetailPk === key);
          if (formRow && formRow.amount === currentRow.amount) {
            this.allModifiedRows.delete(key);
          } else {
            // Still modified
            return true;
          }
        } else {
          // Not on current page, but has modifications - return true
          return true;
        }
      }
    }

    return false;
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(
      this.isFormChanged.bind(this),
      this.unsavedChangesModal,
      this.unsavedChangesContentModal,
      this.translationService
    );
  }

  /**
   * Gets the rows with modified amount values
   * @returns Array of modified rows with their new amount values
   */
  getModifiedRows(): any[] {
    if (!this.initialFormValues || !this.displayedRowsForm) {
      return [];
    }

    const currentValues = this.displayedRowsForm.getRawValue();
    const modifiedRows = [];

    if (currentValues.rows && this.initialFormValues.rows) {
      for (let i = 0; i < currentValues.rows.length; i++) {
        if (i < this.initialFormValues.rows.length) {
          // Only check existing rows
          if (currentValues.rows[i].amount !== this.initialFormValues.rows[i].amount) {
            // Add the modified row with its new amount value
            modifiedRows.push({
              casingCreditRepairDetailPk: currentValues.rows[i].casingCreditRepairDetailPk,
              repairCode: currentValues.rows[i].code,
              repairDescription: currentValues.rows[i].description,
              amount: currentValues.rows[i].amount
            });
          }
        }
      }
    }

    return modifiedRows;
  }

  /**
   * Saves the modified amount values to the server
   */
  saveChanges(): void {
    if (!this.saveEnabled) {
      return;
    }

    // Store changes from current page first
    const modifiedRows = this.getModifiedRows();
    modifiedRows.forEach((row) => {
      // Format the amount before storing
      row.amount = this.formatAmountForSave(row.amount);
      this.allModifiedRows.set(row.casingCreditRepairDetailPk, row);
    });

    // Convert Map to array for API submission
    const allChanges = Array.from(this.allModifiedRows.values());

    if (allChanges.length === 0) {
      // No changes to save
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noChangesToSaveText')
      } as Alert);
      return;
    }

    // Show loading indicator or disable save button
    this.productSaveDisabled = true;
    // this.isLoading = true;
    this.cdr.markForCheck();

    this.productDataService
      .updateCasingRepairDetails(this.casingCreditRepairPk, allChanges)
      .pipe(
        finalize(() => {
          // Re-enable save button when API call completes
          this.productSaveDisabled = false;
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          // Clear the modifications map after successful save
          this.allModifiedRows.clear();
          // Reset the form tracking to prevent unsaved changes popup
          this.initialFormValues = JSON.parse(JSON.stringify(this.displayedRowsForm.getRawValue()));
          this.displayedRowsForm.markAsPristine();

          // Reload the entire component data
          this.reloadComponent();

          // Show success message
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('saveSuccessText')
          } as Alert);
        },
        error: (error) => {
          console.error('Error saving changes:', error);
          // Show error message
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('somethingWentWrongText')
          } as Alert);
        }
      });
  }

  /**
   * Completely reloads the component by making direct API calls to refresh all data
   * This will ensure the table displays the most up-to-date data after a save operation
   */
  reloadComponent(): void {
    this.ngOnInit();
    // Show a loading indicator if needed
    this.productSaveDisabled = true;
    this.isLoading = true;

    // Directly call the API to get the latest data for the current page
    this.productDataService
      .getCasingRepairDetails(this.filterFormBackend.value, this.casingCreditRepairPk, this.currentPage, this.pageSize)
      .subscribe({
        next: (response: any) => {
          // Update the component with fresh data
          this.casingRepairDetails = response.data.casingCreditRepairDetails || [];
          this.pagination = response.data.pagination;
          // Update the form with fresh data
          this.updateDisplayedRowsForm(this.casingRepairDetails);
          this.displayedRowsForm.markAsPristine();
          // Clear allModifiedRows after reload
          this.allModifiedRows.clear();
          // Re-enable save button
          this.productSaveDisabled = false;
          this.isLoading = false;
          // Force change detection to update the UI
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error refreshing table data:', error);
          this.productSaveDisabled = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Custom validator to validate amount format and range
   * - Must not exceed 999.99
   * - Must have valid decimal format (max 2 decimal places)
   */
  private amountValidator() {
    return (control: FormControl) => {
      const value = control.value;

      if (value === null || value === undefined || value === '') {
        return null; // Allow empty values
      }

      const numValue = Number(value);

      // Check if it's a valid number
      if (isNaN(numValue)) {
        return { invalidNumber: true };
      }

      // Check if it exceeds maximum
      if (numValue > 999.99) {
        return { maxAmount: { max: 999.99, actual: numValue } };
      }

      return null;
    };
  }

  /**
   * Format amount for display (e.g., 0 becomes "0.00")
   * Also validates that the value doesn't exceed 999.99
   */
  private formatAmountForDisplay(amount: any): string {
    if (amount === null || amount === undefined || amount === 0 || amount === '') {
      return '0.00';
    }
    const numAmount = Number(amount);
    // Validate that the value doesn't exceed max allowed
    if (isNaN(numAmount) || numAmount > 999.99) {
      return '0.00'; // Default to 0.00 if invalid
    }
    return numAmount.toFixed(2);
  }

  /**
   * Format amount for saving (ensures proper decimal format)
   * If integer like 3, converts to 3.00; if decimal like 5.20, keeps as 5.20
   */
  private formatAmountForSave(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return 0;
    }
    // Return the number rounded to 2 decimal places
    return Math.round(numValue * 100) / 100;
  }

  /**
   * Navigates back to the Casing Credit Repair component
   */
  navigateBack(): void {
    // Check if there are unsaved changes
    // Navigate directly if no unsaved changes
    this.router.navigate(['/home/productData/casing-CreditRepair']);
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  /**
   * Restrict input to 3 integer digits and 2 decimal digits (XXX.XX format).
   * Auto-inserts decimal point after 3 digits.
   * Prevents entry beyond the limit (does not auto-cap).
   * Preserves caret position and keeps FormControl in sync.
   */
  restrictTwoDecimalPlaces(event: Event, item: CasingCreditRepairDetails): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const inputEventType = (event as any)?.inputType ?? '';
    const isDeleting = input.dataset.isDeleting === 'true' || inputEventType.startsWith('delete');

    const originalValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? originalValue.length;

    const cleanValue = (val: string) => {
      if (val === '') {
        return '';
      }
      let v = val.replace(/[^0-9.]/g, ''); // remove invalid chars
      v = v.replace(/(\..*)\./g, '$1'); // keep only first dot

      if (v === '.') {
        return '0.';
      }
      if (v.startsWith('.')) {
        v = '0' + v;
      }

      if (v.includes('.')) {
        const [intPart, fracPart] = v.split('.');
        // Limit integer part to 3 digits
        if (intPart.length > 3) {
          // Reject if trying to enter more than 3 digits before decimal
          return null; // Return null to indicate invalid
        }
        // Limit decimal to 2 digits
        let limitedFracPart = fracPart.slice(0, 2);
        v = intPart + '.' + limitedFracPart;
      } else {
        // If no decimal, limit to 3 digits max
        if (v.length > 3) {
          // Reject if trying to enter more than 3 digits
          return null; // Return null to indicate invalid
        }
        // Auto-insert decimal after 3 digits (but NOT when deleting)
        if (v.length === 3 && !isDeleting) {
          v = v + '.';
        }
        // remove leading zeros only if followed by another digit (so "0" stays "0")
        v = v.replace(/^0+(\d)/, '$1');
      }

      // Final validation: ensure the numeric value doesn't exceed 999.99
      const numVal = Number(v);
      if (!isNaN(numVal) && numVal > 999.99) {
        return null; // Reject if exceeds maximum
      }

      return v;
    };

    let newValue = cleanValue(originalValue);

    // If cleanValue returned null, it means the input tried to exceed the limit - reject it
    if (newValue === null) {
      // Prevent the change by resetting to the original value
      input.value = originalValue;
      return;
    }

    // If deletion intent and the cleaned value is '0', allow it to become empty so backspace can clear.
    if (isDeleting && newValue === '0') {
      newValue = '';
    }

    const originalPrefix = originalValue.slice(0, selectionStart);
    let cleanedPrefix = cleanValue(originalPrefix);
    if (cleanedPrefix === null) {
      cleanedPrefix = originalPrefix;
    }
    if (isDeleting && cleanedPrefix === '0') {
      cleanedPrefix = '';
    }

    // Clear deletion flag so it doesn't affect next non-deleting input
    if (input.dataset.isDeleting) {
      delete input.dataset.isDeleting;
    }

    if (newValue === originalValue) {
      try {
        // ignore if nothing to sync
      } catch {
        // ignore
      }
      return;
    }

    const newCaretPos = Math.min(cleanedPrefix.length, newValue.length);
    input.value = newValue;
    try {
      input.setSelectionRange(newCaretPos, newCaretPos);
    } catch {
      // ignore
    }

    try {
      const index = this.casingRepairDetails.findIndex((row) => row.casingCreditRepairDetailPk === item.casingCreditRepairDetailPk);
      const control = (this.displayedRowsForm.get('rows') as FormArray).at(index).get('amount') as FormControl;
      if (control) {
        // Allow empty string during editing - don't force '0.00'
        control.setValue(newValue, { emitEvent: false });
      }
    } catch {
      // ignore
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle blur event to validate and format the amount
   * Rejects values > 999.99 and formats to 2 decimals
   */
  onAmountBlur(event: Event, item: CasingCreditRepairDetails): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const value = input.value?.trim();

    // If field is empty on blur, set to '0.00'
    if (!value || value === '') {
      input.value = '0.00';
      try {
        const index = this.casingRepairDetails.findIndex((row) => row.casingCreditRepairDetailPk === item.casingCreditRepairDetailPk);
        const control = (this.displayedRowsForm.get('rows') as FormArray).at(index).get('amount') as FormControl;
        if (control) {
          control.setValue('0.00', { emitEvent: false });
        }
      } catch {
        // ignore
      }
    } else {
      // Validate and format the value
      const numValue = Number(value);

      // Check if it's a valid number
      if (isNaN(numValue)) {
        // Invalid number, reset to 0.00
        input.value = '0.00';
        try {
          const index = this.casingRepairDetails.findIndex((row) => row.casingCreditRepairDetailPk === item.casingCreditRepairDetailPk);
          const control = (this.displayedRowsForm.get('rows') as FormArray).at(index).get('amount') as FormControl;
          if (control) {
            control.setValue('0.00', { emitEvent: false });
          }
        } catch {
          // ignore
        }
      } else if (numValue > 999.99) {
        // Value exceeds maximum, reject and reset to 0.00
        input.value = '0.00';
        try {
          const index = this.casingRepairDetails.findIndex((row) => row.casingCreditRepairDetailPk === item.casingCreditRepairDetailPk);
          const control = (this.displayedRowsForm.get('rows') as FormArray).at(index).get('amount') as FormControl;
          if (control) {
            control.setValue('0.00', { emitEvent: false });
          }
        } catch {
          // ignore
        }
      } else {
        // Valid value, format to 2 decimals
        const formattedValue = numValue.toFixed(2);
        input.value = formattedValue;
        try {
          const index = this.casingRepairDetails.findIndex((row) => row.casingCreditRepairDetailPk === item.casingCreditRepairDetailPk);
          const control = (this.displayedRowsForm.get('rows') as FormArray).at(index).get('amount') as FormControl;
          if (control) {
            control.setValue(formattedValue, { emitEvent: false });
          }
        } catch {
          // ignore
        }
      }
    }
    this.cdr.markForCheck();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isFormChanged()) {
      $event.returnValue = true;
    }
  }
}
